import { Check, ChevronDown } from "lucide-react";
import { Select as S } from "radix-ui";
import { type ReactNode, useState } from "react";
import { cn } from "../lib/cn";
import { fieldClass } from "./Input";
import { FilterMenu } from "./OptionMenu";

/** Past this many options a select gets a search row (when every label is text). */
export const SELECT_SEARCH_PAST = 10;

export interface SelectOption {
  value: string;
  label: ReactNode;
  /** Secondary text shown to the right of the label in the list. */
  hint?: ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
  "aria-label"?: string;
  /** A search row over the options. Default: on past 10 options whose labels are text. */
  searchable?: boolean;
  /** Placeholder of the search row. */
  searchLabel?: string;
}

/** Styled replacement for the native select. Same chrome as Input, opaque overlay list. */
export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Select",
  id,
  disabled,
  size = "md",
  className,
  "aria-label": ariaLabel,
  searchable,
  searchLabel,
}: SelectProps) {
  const textLabels = options.every((o) => typeof o.label === "string");
  if (searchable ?? (textLabels && options.length > SELECT_SEARCH_PAST))
    return (
      <SearchableSelect
        options={options}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        placeholder={placeholder}
        id={id}
        disabled={disabled}
        size={size}
        className={className}
        aria-label={ariaLabel}
        searchLabel={searchLabel}
      />
    );
  return (
    <S.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <S.Trigger
        id={id}
        aria-label={ariaLabel}
        className={cn(
          fieldClass,
          "flex items-center justify-between gap-2 pr-2 pl-3 text-left data-[placeholder]:text-ink-3",
          size === "sm" ? "h-7 text-ui" : "h-8",
          className,
        )}
      >
        <span className="truncate">
          <S.Value placeholder={placeholder} />
        </span>
        <S.Icon>
          <ChevronDown className="size-4 text-ink-3" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            "z-50 max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md bg-overlay p-1 text-ink shadow-popover",
            "data-[state=closed]:animate-vd-fade-out data-[state=open]:animate-vd-pop-in",
          )}
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className={cn(
                  "relative flex h-8 cursor-default select-none items-center gap-2 rounded-sm pr-8 pl-2.5 text-body outline-none",
                  "data-[highlighted]:bg-fill-2 data-[disabled]:opacity-45",
                )}
              >
                <span
                  className="min-w-0 flex-1 truncate"
                  title={typeof o.label === "string" ? o.label : undefined}
                >
                  <S.ItemText>{o.label}</S.ItemText>
                </span>
                {o.hint ? <span className="shrink-0 text-caption text-ink-3">{o.hint}</span> : null}
                <S.ItemIndicator className="absolute right-2 flex">
                  <Check className="size-4 text-accent-ink" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

/** The same field over a searchable list: the shared menu, one choice, closes on a pick. */
function SearchableSelect({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Select",
  id,
  disabled,
  size = "md",
  className,
  "aria-label": ariaLabel,
  searchLabel = "Search",
}: Omit<SelectProps, "searchable">) {
  const [own, setOwn] = useState(defaultValue);
  const current = value ?? own;
  const [open, setOpen] = useState(false);
  const picked = options.find((o) => o.value === current);
  return (
    <FilterMenu
      open={open}
      onOpenChange={setOpen}
      label={ariaLabel ?? placeholder}
      search
      searchLabel={searchLabel}
      width={240}
      options={options.map((o) => ({
        value: o.value,
        label: typeof o.label === "string" ? o.label : o.value,
        content: typeof o.label === "string" ? undefined : o.label,
        hint: o.hint,
        disabled: o.disabled,
      }))}
      selected={current === undefined ? [] : [current]}
      onSelect={(v) => {
        if (value === undefined) setOwn(v);
        onValueChange?.(v);
      }}
      trigger={
        <button
          type="button"
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            fieldClass,
            "flex items-center justify-between gap-2 pr-2 pl-3 text-left",
            size === "sm" ? "h-7 text-ui" : "h-8",
            className,
          )}
        >
          <span className={cn("truncate", picked ? "" : "text-ink-3")}>
            {picked ? picked.label : placeholder}
          </span>
          <ChevronDown className="size-4 shrink-0 text-ink-3" aria-hidden />
        </button>
      }
    />
  );
}
